import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { ConfirmationService } from 'primeng/api';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';

import { DeleteLeadSourceTokenGQL, ListIntakeTokensGQL } from '../../../../generated/graphql.generated';
import { FreyaNotificationsService } from '../../../services/freya-notifications.service';

import { LeadSourceActions } from './lead-source.actions';

export const leadSourceLoad = createEffect(
    (
		actions$ = inject(Actions),
		listIntakeTokensGQL$ = inject(ListIntakeTokensGQL),
	) => {
        return actions$.pipe(
            ofType(LeadSourceActions.created, LeadSourceActions.load),
            mergeMap((action) => of(action).pipe(
                exhaustMap((action) => {
                    const fetchFromNetwork = ['[Lead Source] Created'].includes(action.type);

                    return listIntakeTokensGQL$.fetch({}, {
                        fetchPolicy: fetchFromNetwork ? 'network-only' :'cache-first',
                    }).pipe(
                        map((res) => {
                            if (res.loading || !res?.data) {
                                return LeadSourceActions.loading();
                            }

                            return LeadSourceActions.loaded({
                                leadSources: res.data?.tokens?.nodes,
                            });
                        }),
                    );
                }),
                catchError((error) => {
                    return of(LeadSourceActions.loadError({ error }));
                }),
            )),
        );
    },
    { dispatch: true, functional: true },
);

export const leadSourceDeleteClicked = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    confirmationService = inject(ConfirmationService),
) => {
    return actions$.pipe(
        ofType(LeadSourceActions.deleteTokenClicked),
        tap(({ tokenId, used }) => {
            if (!used) {
                store.dispatch(LeadSourceActions.deleteTokenConfirmed({ tokenId }));
                return;
            }

            confirmationService.confirm({
                message: `This lead source has been used ${used} time${used === 1 ? '' : 's'}. Are you sure you want to delete it?`,
                header: 'Delete Lead Source',
                icon: 'pi pi-trash',
                acceptButtonStyleClass: 'p-button-danger',
                rejectButtonStyleClass: 'p-button-text',
                acceptLabel: 'Delete',
                rejectLabel: 'Cancel',
                accept: () => {
                    store.dispatch(LeadSourceActions.deleteTokenConfirmed({ tokenId }));
                },
            });
        }),
    );
}, { functional: true, dispatch: false });

export const leadSourceDelete = createEffect(
    (
        actions$ = inject(Actions),
        deleteLeadSourceTokenGQL$ = inject(DeleteLeadSourceTokenGQL),
        localNotify = inject(FreyaNotificationsService),
    ) => {
        return actions$.pipe(
            ofType(LeadSourceActions.deleteTokenConfirmed),
            mergeMap(({ tokenId }) =>
                deleteLeadSourceTokenGQL$.mutate({ ids: [tokenId] }).pipe(
                    map((result) => {
                        if (!result.data?.deleteTokens) {
                            throw new Error('Delete token was not acknowledged by API');
                        }
                        localNotify.addToast.next({ severity: 'success', summary: 'Lead source deleted' });
                        return LeadSourceActions.deleteTokenSuccess({ tokenId });
                    }),
                    catchError((error) => {
                        localNotify.apolloError('Failed to delete lead source', error);
                        return of(LeadSourceActions.deleteTokenError({ error }));
                    }),
                ),
            ),
        );
    },
    { dispatch: true, functional: true },
);
